import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  fetchCrmOverviewFromLists,
  openPipelineFromStageCounts,
  type CrmListOverviewSnapshot,
} from "@/lib/crmAggregate";

const quickLinks: Array<{ href: string; title: string }> = [
  { href: "/crm/leads", title: "Leads" },
  { href: "/crm/contacts", title: "Contacts" },
  { href: "/crm/accounts", title: "Accounts" },
  { href: "/crm/deals", title: "Deals" },
  { href: "/crm/import", title: "Import" },
];

export function CrmOverviewPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<CrmListOverviewSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await fetchCrmOverviewFromLists();
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load CRM overview.");
        setSnapshot(null);
      } else {
        setError(null);
        setSnapshot(res.snapshot);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pipelineOpen = openPipelineFromStageCounts(snapshot?.stageCounts);
  const stageTotal = snapshot?.stageCounts
    ? Object.values(snapshot.stageCounts).reduce((sum, n) => sum + n, 0)
    : 0;
  const isEmptyOrg =
    Boolean(snapshot) && !error && stageTotal === 0 && (snapshot?.totalOpenDeals ?? 0) === 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 border-b border-slate-200/80 pb-6 dark:border-white/10 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">CRM dashboard</h1>
          <p className="mt-1 max-w-2xl text-slate-600 dark:text-slate-400">
            Pipeline health from your accounts and leads, plus quick entry to CRM modules.
          </p>
        </div>
        <Link
          to={isEmptyOrg ? "/crm/leads/new" : "/crm/leads"}
          className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
        >
          {isEmptyOrg ? "New Lead" : "Open leads"}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading CRM overview…</p> : null}
      {error ? (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100"
          role="alert"
        >
          {error}
        </div>
      ) : null}

      {isEmptyOrg && !loading && !error ? (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/80 px-4 py-3 text-sm text-indigo-950 dark:border-indigo-500/30 dark:bg-indigo-950/30 dark:text-indigo-100">
          Your CRM is empty. Create a lead or import a CSV to get started.
        </div>
      ) : null}

      {snapshot && !error ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {[
            { label: "Open pipeline", value: pipelineOpen, hint: "Named accounts new → negotiation" },
            { label: "Accounts", value: snapshot.namedAccountCount, hint: "Same as Accounts list" },
            { label: "Open leads", value: snapshot.openLeadRecordCount, hint: "Not converted or disqualified" },
            { label: "Unassigned", value: snapshot.unassignedCount, hint: "Needs an owner" },
            { label: "Open deals", value: snapshot.totalOpenDeals, hint: "Across named accounts" },
            {
              label: "Closed won",
              value: snapshot.stageCounts.closed_won ?? 0,
              hint: "Named accounts won",
              accent: true,
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-lg border border-slate-200/90 bg-white px-4 py-3 shadow-sm dark:border-white/10 dark:bg-slate-950/40"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {card.label}
              </p>
              <p
                className={
                  card.accent
                    ? "mt-1 text-2xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-400"
                    : "mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-white"
                }
              >
                {card.value}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{card.hint}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Modules</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Jump into any CRM workspace.</p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {quickLinks.map((item) => (
            <li key={item.href}>
              <Link
                to={item.href}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50/60 dark:border-white/10 dark:bg-slate-950/40 dark:text-slate-200 dark:hover:border-indigo-500/40"
              >
                {item.title}
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
